import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShoppingBag, Flame, Sparkles, Candy, ToyBrick, Gift, Timer, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Seo, { SITE_URL } from "@/components/Seo";
import { useCart } from "@/components/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TAGLINE = "🇨🇦 Ships Across Canada • 🔒 Secure Checkout • 📦 Packed by Us";

const formatPrice = (cents, currency = "cad") =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: currency.toUpperCase() }).format(cents / 100);

const saleActive = (p) =>
  p.sale_price_cents > 0 &&
  p.sale_price_cents < p.price_cents &&
  (!p.sale_ends_at || new Date(p.sale_ends_at) > new Date());

const eff = (p) => (saleActive(p) ? p.sale_price_cents : p.price_cents);

function SaleCountdown({ endsAt, slug }) {
  const [left, setLeft] = useState(new Date(endsAt) - new Date());

  useEffect(() => {
    const t = setInterval(() => setLeft(new Date(endsAt) - new Date()), 1000);
    return () => clearInterval(t);
  }, [endsAt]);

  if (left <= 0) return null;
  const d = Math.floor(left / 86400000);
  const h = Math.floor((left % 86400000) / 3600000);
  const m = Math.floor((left % 3600000) / 60000);
  const s = Math.floor((left % 60000) / 1000);
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <span data-testid={`sale-countdown-${slug}`}
      className="inline-flex w-fit items-center gap-1 rounded-full border-2 border-[#0A0A0A] bg-[#0A0A0A] px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wide text-[#FFD700]">
      <Timer className="h-3 w-3" />
      Sale ends {d > 0 ? `${d}d ` : ""}{pad(h)}:{pad(m)}:{pad(s)}
    </span>
  );
}

function ProductCard({ product, index }) {
  const { add } = useCart();
  const soldOut = product.stock === 0;
  const onSale = saleActive(product);

  const addToCart = () => {
    add({ ...product, price_cents: eff(product) });
    toast.success(`${product.name} added to cart`);
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: 0.06 * (index % 4) }}
      data-testid={`product-card-${product.lookup_key}`}
      className="hard-shadow hard-shadow-hover flex flex-col overflow-hidden rounded-2xl border-2 border-[#0A0A0A] bg-white"
    >
      <Link to={`/product/${product.slug}`} data-testid={`product-link-${product.slug}`} className="block aspect-[4/5] overflow-hidden border-b-2 border-[#0A0A0A] bg-[#FFD700]">
        <img
          src={product.image}
          alt={`${product.name} — ${product.tag} from Canadian Beats Marketplace`}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
        />
      </Link>
      <div className="flex flex-1 flex-col p-5">
        <div className="mb-2.5 flex flex-wrap items-center gap-2">
          <span className={`w-fit rounded-full border-2 border-[#0A0A0A] px-3 py-0.5 text-xs font-bold uppercase tracking-widest ${soldOut ? "bg-[#FF2A54] text-white" : "bg-[#FFD700]"}`}>
            {soldOut ? "Sold Out" : product.stock <= 3 ? `Only ${product.stock} left` : product.tag}
          </span>
          {onSale && !soldOut && (
            <span data-testid={`sale-badge-${product.slug || product.lookup_key}`}
              className="w-fit rounded-full border-2 border-[#0A0A0A] bg-[#FF2A54] px-3 py-0.5 text-xs font-black uppercase tracking-widest text-white">
              Sale
            </span>
          )}
          {onSale && !soldOut && product.sale_ends_at && (
            <SaleCountdown endsAt={product.sale_ends_at} slug={product.slug || product.lookup_key} />
          )}
        </div>
        <h3 className="font-display text-xl font-bold tracking-tight">
          <Link to={`/product/${product.slug}`} className="hover:text-[#FF2A54]">{product.name}</Link>
        </h3>
        <p className="mt-1.5 flex-1 text-sm leading-relaxed text-[#4A4A4A]">{product.blurb}</p>
        <div className="mt-4 flex items-center justify-between gap-2">
          <span data-testid={`product-price-${product.lookup_key}`} className="font-display text-lg font-extrabold">
            {onSale ? (
              <>
                <span className="mr-1.5 text-sm font-bold text-[#4A4A4A] line-through">{formatPrice(product.price_cents, product.currency)}</span>
                <span className="text-[#FF2A54]">{formatPrice(product.sale_price_cents, product.currency)}</span>
              </>
            ) : (
              formatPrice(product.price_cents, product.currency)
            )}
          </span>
          <button
            onClick={addToCart}
            disabled={soldOut}
            data-testid={`add-to-cart-${product.slug || product.lookup_key}`}
            className="hard-shadow hard-shadow-hover inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border-2 border-[#0A0A0A] bg-[#0A0A0A] px-3.5 py-2.5 font-display text-xs font-black uppercase text-white transition-colors hover:bg-[#FF2A54] disabled:opacity-60"
          >
            <ShoppingBag className="h-4 w-4" />
            {soldOut ? "Sold Out" : "Add to Cart"}
          </button>
        </div>
      </div>
    </motion.article>
  );
}

function SectionHeading({ icon: Icon, title, accent }) {
  return (
    <h2 className="flex items-center gap-3 font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl">
      <span className="hard-shadow flex h-11 w-11 items-center justify-center rounded-xl border-2 border-[#0A0A0A] bg-[#FFD700]">
        <Icon className="h-6 w-6 text-[#FF2A54]" />
      </span>
      {title} {accent && <span className="text-[#FF2A54]">{accent}</span>}
    </h2>
  );
}

function Section({ id, icon, title, accent, products, ctas, onPick, testid }) {
  if (products.length === 0) return null;
  return (
    <div id={id} data-testid={testid} className="scroll-mt-24 border-t-2 border-dashed border-[#0A0A0A]/15 pt-10 first:border-0 first:pt-0">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <SectionHeading icon={icon} title={title} accent={accent} />
        <div className="flex flex-wrap gap-2">
          {(ctas || []).map(([label, cat]) => (
            <button key={cat} onClick={() => onPick(cat)} data-testid={`shop-all-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="hard-shadow hard-shadow-hover inline-flex items-center gap-1.5 rounded-full border-2 border-[#0A0A0A] bg-white px-5 py-2 font-display text-xs font-extrabold uppercase hover:bg-[#FFD700]">
              {label} <ArrowRight className="h-3.5 w-3.5" />
            </button>
          ))}
        </div>
      </div>
      <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {products.map((p, i) => (
          <ProductCard key={p.lookup_key} product={p} index={i} />
        ))}
      </div>
    </div>
  );
}

function TrendingBanner() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7 }}
      data-testid="trending-banner"
      className="hard-shadow-lg relative mb-6 overflow-hidden rounded-2xl border-2 border-[#0A0A0A]"
    >
      <img src="/trending-banner.jpg" alt="Hot picks at Canadian Beats Marketplace"
        className="h-32 w-full object-cover sm:h-40" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/40 via-transparent to-transparent" />
      <div className="absolute inset-0 flex items-center px-6 sm:px-10">
        <div data-testid="trending-banner-text" className="hard-shadow rounded-2xl border-2 border-[#0A0A0A] bg-[#FFD700] px-5 py-3 sm:px-7 sm:py-4">
          <p className="font-display text-2xl font-black uppercase leading-none tracking-tighter text-[#0A0A0A] sm:text-4xl">
            Hot picks
          </p>
          <p className="mt-1 font-display text-xs font-extrabold uppercase tracking-wide text-[#0A0A0A]/80 sm:text-sm">
            The treats everyone&rsquo;s grabbing right now
          </p>
        </div>
      </div>
    </motion.div>
  );
}

export default function Shop({ category = null, onPickCategory = () => {} }) {
  const [products, setProducts] = useState([]);
  const [sort, setSort] = useState("featured");

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((res) => setProducts(res.data.products))
      .catch(() => toast.error("Could not load products."));
  }, []);

  const trending = products.filter((p) => p.trending);
  const trendingOrAll = trending.length > 0 ? trending : products;

  const itemListLd = products.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: products.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Product",
        name: p.name,
        image: p.image,
        description: p.blurb,
        url: `${SITE_URL}/product/${p.slug}`,
        brand: { "@type": "Brand", name: "Canadian Beats Marketplace" },
        offers: {
          "@type": "Offer",
          priceCurrency: "CAD",
          price: (eff(p) / 100).toFixed(2),
          availability: p.stock === 0 ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
          url: `${SITE_URL}/product/${p.slug}`,
        },
      },
    })),
  } : null;

  if (category === null) {
    const newest = products.slice().sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")).slice(0, 4);
    const candy = products.filter((p) => p.tag === "Candy").slice(0, 4);
    const toysSensory = products.filter((p) => p.tag === "Toys" || p.tag === "Sensory").slice(0, 4);
    const impulse = products.filter((p) => eff(p) < 500 || p.tag === "Mini Add-Ons").slice(0, 4);
    return (
      <section id="shop" data-testid="shop-section" className="scroll-mt-24 border-b-2 border-[#0A0A0A] py-10 sm:py-14">
        <div className="mx-auto flex max-w-7xl flex-col gap-10 px-5 sm:px-8">
          {itemListLd && <Seo jsonLd={itemListLd} />}
          <p className="text-center font-display text-xs font-extrabold uppercase tracking-widest text-[#0A0A0A] sm:text-sm">{TAGLINE}</p>
          <div>
            <TrendingBanner />
            <Section icon={Flame} title="Trending" accent="now" testid="section-trending"
              products={trendingOrAll.slice(0, 8)} onPick={onPickCategory}
              ctas={[["Shop Trending", "Trending"]]} />
          </div>
          <Section id="new-drops" icon={Sparkles} title="New this" accent="week" testid="section-new-drops"
            products={newest} onPick={onPickCategory} />
          <Section icon={Candy} title="Candy" testid="section-candy"
            products={candy} onPick={onPickCategory} ctas={[["Shop All Candy", "Candy"]]} />
          <Section icon={ToyBrick} title="Toys &" accent="sensory" testid="section-toys-sensory"
            products={toysSensory} onPick={onPickCategory}
            ctas={[["All Toys", "Toys"], ["All Sensory", "Sensory"]]} />
          <Section icon={Gift} title="Under $5 &" accent="mini add-ons" testid="section-impulse"
            products={impulse} onPick={onPickCategory} ctas={[["Shop All Mini Add-Ons", "Mini Add-Ons"]]} />
        </div>
      </section>
    );
  }

  const visible = (
    category === "Trending" ? trendingOrAll : products.filter((p) => p.tag === category)
  )
    .slice()
    .sort((a, b) =>
      sort === "price-asc" ? eff(a) - eff(b)
      : sort === "price-desc" ? eff(b) - eff(a)
      : 0
    );

  return (
    <section id="shop" data-testid="shop-section" className="scroll-mt-24 border-b-2 border-[#0A0A0A] py-10 sm:py-14">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        {category === "Trending" && <TrendingBanner />}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="flex flex-wrap items-end justify-between gap-4"
        >
          <h2 data-testid="shop-heading" className="font-display text-4xl font-extrabold uppercase tracking-tight sm:text-5xl">
            {category === "Trending" ? (
              <>Trending <span className="text-[#FF2A54]">now</span></>
            ) : (
              <span className="text-[#FF2A54]">{category}</span>
            )}
          </h2>
          <div className="flex flex-wrap items-center gap-4">
            <p className="max-w-xs font-display text-xs font-extrabold uppercase tracking-wide text-[#0A0A0A]">{TAGLINE}</p>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="shop-sort-select"
              className="hard-shadow cursor-pointer rounded-full border-2 border-[#0A0A0A] bg-white px-5 py-2 font-display text-xs font-extrabold uppercase outline-none focus:ring-4 focus:ring-[#FF2A54]/40"
            >
              <option value="featured">Featured</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
            </select>
          </div>
        </motion.div>

        <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((p, i) => (
            <ProductCard key={p.lookup_key} product={p} index={i} />
          ))}
        </div>
        {visible.length === 0 && products.length > 0 && (
          <p data-testid="shop-empty-filter" className="mt-10 text-sm font-medium text-[#4A4A4A]">
            Nothing in this category yet — check back soon!
          </p>
        )}
      </div>
    </section>
  );
}
